import * as React from 'react';
import { Row, Col } from 'react-bootstrap';

import MapField, { AddressType, AddressTypeLabel } from 'af-components/Maps/WorkLocationAddressMap';
import Tooltip from 'af-components/Tooltip';

import styles from './styles.module.scss';

interface FieldColumnWidthValues {
	fieldColumnWidth: number;
	deleteBtnColumnWidth: number;
}

const addressTypeColumnWidthValues: { [key in keyof typeof AddressType]: FieldColumnWidthValues; } = {
	[AddressType.US]: {
		fieldColumnWidth: 14,
		deleteBtnColumnWidth: 10,
	},
	[AddressType.CAN]: {
		fieldColumnWidth: 14,
		deleteBtnColumnWidth: 10,
	},
	[AddressType.OTHER]: {
		fieldColumnWidth: 22,
		deleteBtnColumnWidth: 2,
	},
};

interface OwnProps {
	index?: number;
	locationPropName: string;
	streetNumberPropName: string;
	routePropName: string;
	cityPropName: string;
	neighborhoodPropName: string;
	countryPropName: string;
	backupPropName: string;
	postalCodePropName: string;
	statePropName: string;
	notesPropName: string;
	removeAddress?: (index: number) => void;
	clearAddress?: (index?: number) => void;
	formName: string;
	disableMap?: boolean;
	autosave?: () => Promise<void>;
	disableRemove?: boolean;
	disabled?: boolean;
	onValueUpdate?: () => void;
	// Deliberate like this undefined used to distinguish new address and no country
	country: Nullable<string> | undefined;
	latitude: Nullable<number>;
	longitude: Nullable<number>;
}

type Props = OwnProps;

const _getCountryType = (country: Nullable<string> | undefined) => {
	switch (country) {
		// US and new address se to US
		case AddressTypeLabel.US:
		case undefined:
			return AddressType.US;
		case AddressTypeLabel.CAN:
			return AddressType.CAN;
		// null means other country
		default:
			return AddressType.OTHER;
	}
};

const AddressField: React.FC<Props> = (props) => {
	const { removeAddress, clearAddress, country, index, disableRemove, disabled } = props;

	const [addressType, setAddressType] = React.useState(_getCountryType(country));

	React.useEffect(() => {
		setAddressType(_getCountryType(country));
	}, [country]);

	const removeThisAddress = React.useCallback(() => {
		if (removeAddress && index !== undefined) {
			removeAddress(index);
		}
	}, [index, removeAddress]);

	const onClearAddress = React.useCallback(() => {
		clearAddress?.(index);
	}, [clearAddress, index]);

	const renderButton = React.useCallback(() => {
		if (disableRemove) {
			return (
				<Tooltip message="Clear" >
					<div
						className={styles['delete-btn']}
						onClick={onClearAddress}
					>
						<div className="icon-close" />
						<div>Clear</div>
					</div>
				</Tooltip >
			);
		}
		return (
			<Tooltip message="Delete">
				<div
					className={styles['delete-btn']}
					onClick={removeThisAddress}
				>
					<div className="icon-delete" />
					<div>Delete</div>
				</div>
			</Tooltip>
		);
	}, [disableRemove, onClearAddress, removeThisAddress]);

	return (
		<>
			<Row className={styles.container}>
				<Col className="p-l-0" sm={addressTypeColumnWidthValues[addressType].fieldColumnWidth}>
					<MapField
						addressType={addressType}
						onAddressTypeChange={setAddressType}
						{...props}
					/>
				</Col>
				{!disabled &&
					<Col className={styles['remove-btn-container']} sm={addressTypeColumnWidthValues[addressType].deleteBtnColumnWidth}>
						{renderButton()}
					</Col>
				}
			</Row>
			{!disabled &&
				<Row>
					<div className={styles.addresses__hint}>
						Please Fill out address or notes for the form to submit.
					</div>
				</Row>
			}
		</>
	);
};

export default React.memo(AddressField);
